import { RoleService } from '../../api/services/roleService';
import { getCookie } from '../../utils/cookies';
import { extractTotalCount } from '../../components/common/tableStyles';

export const permissions = {
    state: {
        list: [], // This is the permissions matrix or raw list
        matrix: null, // Structured permissions matrix
        total: 0,
        isLoading: false,
        error: null,
    },
    reducers: {
        setPermissions(state, payload) {
            let list = [];
            let matrix = null;
            const data = payload.data || payload;
            
            if (Array.isArray(data)) {
                // Direct array of permission objects
                list = data;
            } else if (typeof data === 'object' && data !== null) {
                // API returns: { roles: [...], availablePermissions: ["CAN_...", ...] }
                const availablePermissions = data.availablePermissions || [];
                if (availablePermissions.length > 0) {
                    // Store the flat permission strings as the matrix for RoleFormModal
                    matrix = availablePermissions;
                    
                    // Build the list for the Permissions Matrix tab display
                    availablePermissions.forEach((perm, index) => {
                        if (typeof perm === 'string') {
                            list.push({
                                id: `perm_${index}`,
                                key: perm,
                                name: perm === '*' ? 'All Permissions (Wildcard)' : perm.replace(/_/g, ' ').replace(/^CAN /, ''),
                                description: perm,
                                category: perm === '*' ? 'SYSTEM' : 'PERMISSIONS',
                            });
                        }
                    });
                } else {
                    // Fallback: treat as { CATEGORY: { KEY: VALUE } } matrix
                    matrix = data;
                    Object.entries(data).forEach(([category, perms]) => {
                        if (typeof perms === 'object' && perms !== null && !Array.isArray(perms)) {
                            Object.entries(perms).forEach(([key, value]) => {
                                list.push({
                                    id: `${category}_${key}`,
                                    key: key,
                                    name: typeof value === 'string' ? value : key,
                                    description: key.replace(/_/g, ' '),
                                    level: category,
                                    category: category,
                                });
                            });
                        }
                    });
                }
            }
            const total = extractTotalCount(payload, list);
            return { ...state, list, matrix, total };
        },
        setLoading(state, payload) {
            return { ...state, isLoading: payload };
        },
        setError(state, payload) {
            return { ...state, error: payload };
        },
    },
    effects: (dispatch) => ({
        async fetchPermissionsAsync(params = {}) {
            dispatch.permissions.setLoading(true);
            try {
                // Here we fetch the permissions matrix
                const response = await RoleService.getPermissionsMatrix(params);
                const data = response.data || response;
                
                // If the response contains roles, sync them with the users model
                if (data.roles && data.roles.length > 0) {
                    dispatch.users.setRoles(data.roles);
                }
                
                dispatch.permissions.setPermissions(data);
            } catch (error) {
                console.error("Failed to fetch permissions", error);
                dispatch.permissions.setError(error.message);
            } finally {
                dispatch.permissions.setLoading(false);
            }
        },
        async updatePermissionsMatrixAsync(matrixData) {
            dispatch.permissions.setLoading(true);
            try {
                const authData = JSON.parse(getCookie('adminAuth') || '{}');
                const tenantId = authData.tenantId || '1';
                await RoleService.updatePermissionsMatrix(matrixData, tenantId);
                // Refetch
                await dispatch.permissions.fetchPermissionsAsync();
                return { success: true };
            } catch (error) {
                console.error("Failed to update permissions matrix", error);
                throw error;
            } finally {
                dispatch.permissions.setLoading(false);
            }
        },
        async createRoleAsync(roleData) {
            dispatch.permissions.setLoading(true);
            try {
                await RoleService.create(roleData);
                dispatch.users.fetchRolesAsync(); // Refresh roles list in users model
                return { success: true };
            } catch (error) {
                console.error("Failed to create role", error);
                throw error;
            } finally {
                dispatch.permissions.setLoading(false);
            }
        },
        async updateRoleAsync({ id, data }) {
            dispatch.permissions.setLoading(true);
            try {
                await RoleService.update(id, data);
                dispatch.users.fetchRolesAsync();
                return { success: true };
            } catch (error) {
                console.error("Failed to update role", error);
                throw error;
            } finally {
                dispatch.permissions.setLoading(false);
            }
        },
        async deleteRoleAsync(id) {
            dispatch.permissions.setLoading(true);
            try {
                await RoleService.delete(id);
                dispatch.users.fetchRolesAsync();
                return { success: true };
            } catch (error) {
                console.error("Failed to delete role", error);
                throw error;
            } finally {
                dispatch.permissions.setLoading(false);
            }
        }
    })
};
